import { Button, Typography } from '@breetometer/components';
import { CopyIcon, DownloadIcon, ReportIcon, ResolveIcon, ShareIcon } from 'BreetAssets';
import { copyToClipboard } from 'BreetHelpers';
import { useComponentVisibilityApi, useModalApi } from 'BreetHooks';
import { useCallback, useEffect, useRef, useState } from 'react';

import {
	copyReceiptImage,
	downloadReceiptImage,
	downloadReceiptPdf,
	type IEngagementActions,
	shareContent,
} from './EngagementActions.helpers';

const EngagementActions = ({ isResolve, onResolve, shareContentDetails, noReport, isCopy, copy }: IEngagementActions) => {
	const { handleOpenModal } = useModalApi();
	const { isMobile } = useComponentVisibilityApi();

	const [isSharing, setIsSharing] = useState(false);
	const [isMenuOpen, setIsMenuOpen] = useState(false);
	const shareRef = useRef<HTMLDivElement>(null);

	const isShareAction = shareContentDetails?.action === 'share';
	const hasShareMenu = isShareAction && !isMobile;

	useEffect(() => {
		if (!isMenuOpen) return undefined;

		const handlePointerDown = (event: PointerEvent) => {
			if (shareRef.current && !shareRef.current.contains(event.target as Node)) setIsMenuOpen(false);
		};
		const handleKeyDown = (event: KeyboardEvent) => {
			if (event.key === 'Escape') setIsMenuOpen(false);
		};

		document.addEventListener('pointerdown', handlePointerDown);
		document.addEventListener('keydown', handleKeyDown);

		return () => {
			document.removeEventListener('pointerdown', handlePointerDown);
			document.removeEventListener('keydown', handleKeyDown);
		};
	}, [isMenuOpen]);

	const runShareAction = useCallback(
		(action: () => Promise<void>) => async () => {
			setIsMenuOpen(false);
			setIsSharing(true);
			try {
				await action();
			} catch (e: unknown) {
				console.error(e);
			} finally {
				setIsSharing(false);
			}
		},
		[]
	);

	const handlePrimaryClick = useCallback(() => {
		if (!isShareAction) {
			runShareAction(downloadReceiptImage(shareContentDetails))().catch(console.error);
			return;
		}

		if (isMobile) {
			runShareAction(shareContent(shareContentDetails))().catch(console.error);
			return;
		}

		setIsMenuOpen((open) => !open);
	}, [isShareAction, isMobile, shareContentDetails, runShareAction]);

	const shareDefaultTitle = isShareAction ? 'Share' : 'Download';

	return (
		<div
			className='engagement'
			data-testid='engagement-actions'
		>
			<div
				className='engagement_share'
				ref={shareRef}
			>
				<Button
					variant='native'
					className='engagement_item'
					onClick={handlePrimaryClick}
					disabled={isSharing}
					aria-busy={isSharing}
					aria-haspopup={hasShareMenu ? 'menu' : undefined}
					aria-expanded={hasShareMenu ? isMenuOpen : undefined}
				>
					{isShareAction ? (
						<ShareIcon className='svg-dark svg-dark-outline' />
					) : (
						<DownloadIcon className='svg-dark svg-dark-outline' />
					)}
					<Typography className='color-primary-50'>{isSharing ? 'Preparing…' : shareDefaultTitle}</Typography>
				</Button>

				{hasShareMenu && isMenuOpen && (
					<div
						className='engagement_share-menu'
						role='menu'
					>
						<button
							type='button'
							role='menuitem'
							onClick={runShareAction(copyReceiptImage(shareContentDetails))}
						>
							<CopyIcon
								variant='asset'
								className='svg-dark svg-dark-outline'
							/>
							<Typography className='color-primary-50'>Copy image</Typography>
						</button>
						<button
							type='button'
							role='menuitem'
							onClick={runShareAction(downloadReceiptImage(shareContentDetails))}
						>
							<DownloadIcon className='svg-dark svg-dark-outline' />
							<Typography className='color-primary-50'>Save as PNG</Typography>
						</button>
						<button
							type='button'
							role='menuitem'
							onClick={runShareAction(downloadReceiptPdf(shareContentDetails))}
						>
							<DownloadIcon className='svg-dark svg-dark-outline' />
							<Typography className='color-primary-50'>Save as PDF</Typography>
						</button>
					</div>
				)}
			</div>

			{isResolve && (
				<Button
					variant='native'
					className='engagement_item'
					onClick={onResolve}
				>
					<ResolveIcon className='svg-dark svg-dark-outline' />
					<Typography className='color-primary-50'>Resolve</Typography>
				</Button>
			)}

			{!noReport && (
				<Button
					variant='native'
					className='engagement_item'
					onClick={handleOpenModal({ id: 'contactSupport' })}
				>
					<ReportIcon className='svg-dark svg-dark-outline' />
					<Typography className='color-primary-50'>Report</Typography>
				</Button>
			)}

			{isCopy && (
				<Button
					variant='native'
					className='engagement_item'
					onClick={copy?.copyFn ?? copyToClipboard(copy?.item, copy?.message)}
				>
					<CopyIcon
						variant='asset'
						className='svg-dark svg-dark-outline'
					/>
					<Typography className='color-primary-50'>Copy</Typography>
				</Button>
			)}
		</div>
	);
};

export default EngagementActions;
